// apps/provider/src/services/api/feedback.js
// Patient Feedback dashboard (Xano GET /feedback/dashboard) and the paged
// per-therapist responses list (GET /feedback/responses). Rows come from the
// feedback_score table (one row per rated target: a therapist, the front
// desk with subject 'front_desk', or the overall experience with subject
// 'overall'; provider_id sentinels __unassigned__, __front_desk__ and
// __overall__), joined to form_responses for first_name / last_name and the
// internal note, plus form_send_log for response rate.
// Org-gated client-side by organization.settings.feedback_enabled; access is
// enforced server-side by functions/resolve_feedback_access (feedback_view_all
// = every therapist, feedback_view_own = clamped to the caller).
//
// Methods: getFeedbackDashboard, getFeedbackResponses, getFeedbackPatientResponses, setFeedbackResponseNote
//
// Response types: @prism-ehr/shared → FeedbackDashboardResponse, FeedbackResponsesResponse
import { retryOnBusy } from '../../utils/retryOnBusy';

/**
 * @returns {Object} Feedback domain methods (bound to `this` of the composed apiService)
 */
export function createFeedbackMethods() {
  return {
    /**
     * Feedback dashboard for a date window: feedback forms + scoring config,
     * score histogram per therapist, send/submitted counts. Individual responses
     * come from getFeedbackResponses. Sits behind the reporting admission gate
     * (429 → retryOnBusy).
     *
     * Xano: GET /feedback/dashboard
     * Xano file: feedback-dashboard_GET.xs
     *
     * @param {{ start_date: string, end_date: string, nocache?: boolean }} filters YYYY-MM-DD window
     * @param {{ signal?: AbortSignal }} [options]
     * @returns {Promise<import('@prism-ehr/shared').FeedbackDashboardResponse>}
     */
    getFeedbackDashboard(filters = {}, options = {}) {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.nocache) params.append('nocache', '1');
      const query = params.toString();
      return retryOnBusy(() => this.request(`/feedback/dashboard${query ? `?${query}` : ''}`, {
        method: 'GET',
        timeoutMs: 90000,
        signal: options.signal
      }), { signal: options.signal });
    },

    /**
     * One page of a therapist's scored feedback responses inside a date window
     * (patient name, form, score, comment). provider_id is a user id or the
     * '__unassigned__', '__front_desk__' or '__overall__' sentinel; under
     * feedback_view_own it must be the caller.
     * Sits behind the reporting admission gate (429 → retryOnBusy).
     *
     * Xano: GET /feedback/responses
     * Xano file: feedback-responses_GET.xs
     *
     * `all: true` (feedback_view_all only) returns every provider's responses instead of one bucket;
     * `sort` = created_desc (default) | created_asc | score_desc | score_asc.
     *
     * @param {{ provider_id?: string, all?: boolean, sort?: string, form_id?: string, start_date: string, end_date: string, page?: number, per_page?: number, nocache?: boolean }} filters
     * @param {{ signal?: AbortSignal }} [options]
     * @returns {Promise<import('@prism-ehr/shared').FeedbackResponsesResponse>}
     */
    getFeedbackResponses(filters = {}, options = {}) {
      const params = new URLSearchParams();
      if (filters.provider_id) params.append('provider_id', filters.provider_id);
      if (filters.all) params.append('all', '1');
      if (filters.sort) params.append('sort', filters.sort);
      if (filters.form_id) params.append('form_id', filters.form_id);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.page) params.append('page', String(filters.page));
      if (filters.per_page) params.append('per_page', String(filters.per_page));
      if (filters.nocache) params.append('nocache', '1');
      const query = params.toString();
      return retryOnBusy(() => this.request(`/feedback/responses${query ? `?${query}` : ''}`, {
        method: 'GET',
        timeoutMs: 90000,
        signal: options.signal
      }), { signal: options.signal });
    },

    /**
     * Staff-only internal note on a feedback response (feedback_view_all).
     * Empty string clears it.
     *
     * Xano: PATCH /feedback/response-note
     * Xano file: feedback-response-note_PATCH.xs
     *
     * @param {string} formResponseId
     * @param {string} internalNote
     * @returns {Promise<{ id: string, internal_note: string, internal_note_by: string, internal_note_at: number }>}
     */
    /**
     * Every scored feedback response for one patient, all time (Feedback area
     * patient drill-down). Under feedback_view_own the server returns only rows
     * attributed to the caller. Rows carry the raw form_responses columns
     * (feedback_score, feedback_comment, first_name...) without answers.
     *
     * Xano: GET /feedback/patient-responses
     * Xano file: feedback-patient-responses_GET.xs
     *
     * @param {string} patientId
     * @returns {Promise<import('@prism-ehr/shared').FeedbackPatientResponsesResponse>}
     */
    getFeedbackPatientResponses(patientId) {
      return this.request(`/feedback/patient-responses?patient_id=${encodeURIComponent(patientId)}`, { method: 'GET' });
    },

    setFeedbackResponseNote(formResponseId, internalNote) {
      return this.request('/feedback/response-note', {
        method: 'PATCH',
        body: JSON.stringify({ form_response_id: formResponseId, internal_note: internalNote ?? '' })
      });
    },
  };
}
